import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { View, YStack, Button, Separator } from "tamagui";
import TextCoustom, { TextSize } from "@components/TextCoustom";
import { colors } from "@styles/colors";
import * as basicStyle from "@styles/index";
import Header from "../components/Header";
import { getDraftListWithDetails, type DraftListItem } from "../utils/mockApi";
import { formatDateTime } from "@utils/func/timeFormatter";
import { useAuthReady } from "@provider/LineLoginProvider";
import { MdOutlineEdit, MdSchedule } from "react-icons/md";
import TextCustom from "@components/TextCoustom";

export default function DraftList() {
  const authReady = useAuthReady();
  const navigate = useNavigate();
  const [list, setList] = useState<DraftListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authReady) return;
    const fetchList = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await getDraftListWithDetails();
        setList(items);
      } catch (err: any) {
        setError(err?.message || "載入草稿列表失敗");
      } finally {
        setIsLoading(false);
      }
    };
    fetchList();
  }, [authReady]);

  const handleDraftClick = (draftId: string) => {
    navigate(`/quickOrder/${draftId}`);
  };

  const handleQuickDelivery = (draftId: string) => {
    navigate(`/quickOrder/${draftId}/delivery`);
  };

  return (
    <View flex={1} w="100%" minHeight="100dvh" bg="#FFF">
      <Header title="我的草稿訂單" />

      <View flex={1} px="$7" py="$4" paddingBottom="$6">
        {!authReady && (
          <View
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={100}
            bg="rgba(0,0,0,0.4)"
            alignItems="center"
            justifyContent="center">
            <View bg="#FFF" borderRadius="$4" p="$6" alignItems="center" gap="$3">
              <TextCoustom type={TextSize.MEDIUM} str="載入中..." color={colors.subBlue} />
            </View>
          </View>
        )}

        {isLoading && authReady && (
          <View py="$8" alignItems="center">
            <TextCoustom type={TextSize.MEDIUM} str="載入中..." color={colors.subBlue} />
          </View>
        )}

        {error && (
          <View py="$8" alignItems="center">
            <TextCoustom type={TextSize.MEDIUM} str={error} color={colors.darkRed || "#DC2626"} others={{ textAlign: "center" }} />
          </View>
        )}

        {!isLoading && !error && list.length === 0 && (
          <View flex={1} justifyContent="center" alignItems="center" py="$8">
            <YStack alignItems="center" gap="$2">
              <TextCoustom type={TextSize.SUBHEADING} str="目前沒有草稿訂單" color={colors.dakerBlue} bold />
              <TextCoustom type={TextSize.NORMAL} str="建立新訂單後會顯示在此" color={colors.subBlue} />
            </YStack>
          </View>
        )}

        {!isLoading && !error && list.length > 0 && (
          <YStack gap="$4" w="100%">
            <YStack gap="$1" mb="$2">
              <TextCoustom type={TextSize.SUBNORMAL} str={`共有 ${list.length} 筆草稿訂單`} color={colors.dakerBlue} align="right"/>
            </YStack>

            {list.map((item, index) => (
              <View
                key={item.draftId}
                bg={colors.bgGray2 ?? "#F5F5F5"}
                borderRadius="$4"
                p="$4"
                borderWidth={1}
                borderColor={colors.dividerGray ?? "#E5E5E5"}>
                <YStack w="100%" gap="$2" alignItems="flex-start" mb="$3">
                  <View flexDirection="row" alignItems="center" justifyContent="space-between" w="100%">
                    <TextCoustom type={TextSize.MEDIUM} str={`草稿訂單 #${index + 1}`} color={colors.dakerBlue} bold />
                    {item.order_status && (
                      <View
                        bg={
                          item.order_status.includes("取消")
                            ? colors.darkRed
                            : item.order_status.includes("完成")
                            ? colors.lineGreen
                            : colors.lightOrange
                        }
                        px="$2"
                        py="$1"
                        borderRadius="$2">
                        <TextCoustom type={TextSize.SUBSMALL} str={item.order_status} color="#FFF" />
                      </View>
                    )}
                  </View>
                  {item.created_at && (
                    <View flexDirection="row" alignItems="center" gap="$1">
                      <MdSchedule size={14} color={colors.subBlue} style={{ flexShrink: 0 }} />
                      <TextCoustom type={TextSize.SMALL} str={`建立時間 ${formatDateTime(item.created_at)}`} color={colors.subBlue} />
                    </View>
                  )}
                  <TextCoustom type={TextSize.SUBSMALL} str={item.draftId} color={colors.subBlue} align="left" others={{ opacity: 0.85 }} />
                </YStack>

                <View flexDirection="column" gap="$3" w="100%">
                  <View flex={1} minWidth={0}>
                    <Button
                      {...basicStyle.ShareBlueButton}
                      bg={"$colorTransparent"}
                      bw={"1px"}
                      bc={colors.btnBlue}
                      color={colors.btnBlue}
                      size="$3"
                      w="100%"
                      icon={<MdSchedule size={16} />}
                      onPress={() => handleQuickDelivery(item.draftId)}>
                      <TextCustom type={TextSize.SUBSMALL} str="指定配送時間" color={colors.btnBlue} bold />
                    </Button>
                  </View>
                  <View flex={1} minWidth={0}>
                    <Button
                      {...basicStyle.ShareBlueButton}
                      bg={colors.btnBlue}
                      icon={<MdOutlineEdit size={16} />}
                      color="#FFF"
                      size="$3"
                      w="100%"
                      onPress={() => handleDraftClick(item.draftId)}>
                      <TextCustom type={TextSize.SUBSMALL} str="修改訂單內容" color="#FFF" bold />
                    </Button>
                  </View>
                </View>
              </View>
            ))}
          </YStack>
        )}
      </View>
    </View>
  );
}
